import React, { useState, useEffect, useCallback } from 'react';
import { InputLabel, MenuItem, Select, FormControl, CircularProgress, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import provisionService from '../../../services/ProvisionService';
import { SchoolListItem } from '@beable/roster-backend-client-v1';
import { getStyles } from '../../../theme/styles';

interface ProvisionSelectProps {
  siteId: number;
  schoolId?: string;
  onChange: any;
  allOption?: boolean;
  disabled?: boolean;
  className?: string;
  label?: string;
  variant?: 'filled' | 'outlined';
  omitSchool?: string;
  inPanel?: boolean;
}

export default function SchoolSelector({ siteId, schoolId, onChange, allOption, disabled, className, label, variant, omitSchool, inPanel }: ProvisionSelectProps) {
  const theme = useTheme();
  const styles = getStyles(theme);

  const [schools, setSchools] = useState<SchoolListItem[]>([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const memoOnSelect = useCallback(onChange, []);

  useEffect(() => {
    const fetchSchools = async () => {
      setSelectedSchoolId(undefined);

      const data = await provisionService.getSchools(siteId);
      if (data) {
        setSchools(data);

        let selectedId;
        if (data.length === 1) {
          selectedId = data[0].id;
        }

        setSelectedSchoolId(schoolId ?? selectedId);
        memoOnSelect(schoolId ?? selectedId);
      }
      else {
        setSchools([]);
        memoOnSelect(undefined);
      }

      setIsLoading(false)
    };

    setIsLoading(true)
    fetchSchools();
  }, [siteId, memoOnSelect, schoolId]);

  const handleChange = (event: any) => {
    setSelectedSchoolId(event.target.value);
    if (onChange) {
      onChange(event.target.value);
    }
  }

  return <>
    {isLoading && (
      <Box sx={styles.loaderContainer}>
        <CircularProgress disableShrink />
      </Box>
    )}
    {!isLoading && (
      <FormControl fullWidth className={className} sx={inPanel ? styles.pFormControlPanel : (className ? undefined : styles.pFormControl)} variant={variant || "standard"}>
        <InputLabel htmlFor='schoolId'>{ siteId && !schools.length ? 'No Schools Found' : (label || 'Select School')}</InputLabel>
        <Select
          variant={ variant || "standard" }
          value={(schools.length && selectedSchoolId) || ''}
          onChange={handleChange}
          inputProps={{
            'name': 'schoolId',
            'id': 'schoolId',
            'data-testid': 'test-schoolId',
          }}
          disabled={schools.length <= 1 || disabled}>
          {allOption &&
          <MenuItem
            value='ALL'
            role='option'
          >All Schools</MenuItem>
          }
          {
            schools.length &&
            schools.filter((s) => s.id !== omitSchool).map((school: any) => (
              <MenuItem key={school.id} value={school.id} role='option'>
                {school.name}
              </MenuItem>
            ))
          }
          {
            siteId && !schools.length &&
            <MenuItem key={0} value={0} role='option'>
                No Schools found
            </MenuItem>
          }
        </Select>
        </FormControl>
      )}
      </>
    ;
}
